import { memo, useState } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { LoadingMode } from 'components/LoadingProvider'
import HotlineFallback from 'components/HotlineFallback'
import { useErrorState } from 'context/Error/hook'
import CategoryLayout from 'components/Layout/Category'
import { useLoadingMode } from 'stores/utils/hooks/useLoadingMode'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { Button, Link, Theme, Typography } from '@mui/material'
import { NavigateFunction } from 'react-router-dom'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { useStores } from 'stores/utils/hooks/useStores'
import { PREFILL_PAGE_NAMES } from 'utils/pages'
import ResendPin from './ResendPin'
import { PIN_LENGTH } from './AuthenticatePin'
import TimeoutTrigger from './TimeoutTrigger'
import {
  decideNextPageAfterDobAuth,
  isPrefillDobAuthCountExceeded,
  trackDobCheck,
  trackPinCheck
} from './utils'
import { trackTeal } from 'utils/tracking'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { isPrefillAuthCountExceeded } from './utils'
import { PrefillErrorKind } from 'stores/PrefillStore'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { AxiosError } from 'axios'
import { useAsyncEffect } from 'hooks/useAsyncEffect'
import PreviousPageErrors from 'components/PageErrors/PreviousPageErrors'
import { DOB_LENGTH } from './AuthenticateDob'
import { convertFormat } from 'components/PathDateInput/helpers'
import { useRootStore } from 'stores/utils/hooks/useRootStore'

export const CONTINUE_BUTTON_TEXT: Record<
  (typeof PREFILL_PAGE_NAMES)[number],
  string
> = {
  requestPin: 'PIN anfordern',
  authenticatePin: 'PIN prüfen',
  verifiedPin: 'Direkt zu den Angeboten',
  authenticateDob: 'Weiter',
  overview: 'Weiter'
}

export const getButtonSizes = (isBackButtonHidden: boolean) => {
  const sizeOfContinueButton = 12
  const sizeOfSecondaryButton = isBackButtonHidden ? 12 : 8
  const sizeOfBackButton = isBackButtonHidden ? 0 : 4

  return {
    sizeOfContinueButton,
    sizeOfSecondaryButton,
    sizeOfBackButton
  }
}

type Props = {
  navigate: NavigateFunction
  currentPageInSession: string | null
  isSmava: boolean
  onBack?: () => void
  onNext: () => void
}

const styles = (theme: Theme) => ({
  flexBasisGrid: {
    flexBasis: 'auto'
  },
  button: {
    maxHeight: '48px',

    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.action.disabledBackground} !important`
    }
  }
})

const PrefillFooter = observer(
  ({ onNext, currentPageInSession, onBack, navigate }: Props) => {
    const {
      state: { message },
      hasCooldown
    } = useErrorState()
    const {
      fieldErrors,
      prefill,
      history,
      traversal,
      traversal: {
        data: {
          system: { advertisementId }
        },
        traversalId
      }
    } = useStores()
    const { rootStore } = useRootStore()
    const { ref, inView } = useInView()
    const clientDetails = useClientDetails()

    useDebouncedEffect(
      () => {
        if (inView) {
          trackInAmplitude(AmplitudeEvent.CtaIsVisible)
        }
      },
      300,
      [inView]
    )

    const loadingMode = useLoadingMode()

    const { flexBasisGrid, button } = useEmotionStyles(styles, 'primary')
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const sendPin = async () => {
      try {
        await prefill.getPrefillPin(advertisementId, traversalId, 'email')
        prefill.setIsPrefillSkipped(false)
        onNext()
      } catch (err) {
        const errorObj = err as AxiosError
        const status: number | undefined = errorObj?.status

        switch (status) {
          case 429:
            prefill.setIsError(true)
            prefill.setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
            break
          case 500:
            prefill.setIsError(true)
            prefill.setIsPrefillSkipped(false)
            prefill.setErrorKind(PrefillErrorKind.TECHNICAL)
            break
          default:
            prefill.setIsError(true)
            prefill.setIsPrefillSkipped(false)
            prefill.setErrorKind(PrefillErrorKind.TECHNICAL)
            break
        }

        logAndTrackError(
          new Error(`/prefill/pin/send failed. Error status: ${status}`)
        )
      }
    }

    const trackPrefill = (
      event: AmplitudeEvent,
      payload?: Record<string, unknown>
    ) => {
      trackTeal({
        transaction: {
          event
        },
        payload,
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          userAttributes: {
            advertisementId
          }
        }
      })
    }

    const handleContinueButtonClick = async () => {
      trackInAmplitude(AmplitudeEvent.PrefillPrimaryButtonClicked, {
        isError: prefill.isError
      })

      rootStore.setLoadingMode(LoadingMode.Blocking)

      try {
        if (prefill.isError) {
          trackPrefill(AmplitudeEvent.PrefillResumeAfterFailure)
          await traversal.patch([
            { op: 'set', path: '/common/hasAcceptedTerms', value: true }
          ])
          traversal.acceptTermsAndConditions()
          onNext()
          return
        }

        switch (currentPageInSession) {
          case 'requestPin': {
            trackPrefill(AmplitudeEvent.PrefillRequestPinClicked)
            await sendPin() // sendPin handles its own errors internally
            break
          }

          case 'authenticatePin': {
            trackPrefill(AmplitudeEvent.PrefillAuthPinClicked)
            if (isPrefillAuthCountExceeded()) {
              prefill.setIsError(true)
              prefill.setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
              break
            }
            await prefill.postPrefillPinAuth(
              advertisementId,
              traversalId,
              prefill.pin
            )
            onNext()
            break
          }

          case 'verifiedPin': {
            trackPrefill(AmplitudeEvent.PrefillGoToOffersClicked)
            triggerOnNextWithFinalize()
            break
          }

          case 'authenticateDob': {
            trackPrefill(AmplitudeEvent.PrefillAuthDobClicked)
            if (isPrefillDobAuthCountExceeded()) {
              prefill.setIsError(true)
              prefill.setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
              break
            }
            const convertedDob = convertFormat(
              prefill.dob,
              'dd.MM.yyyy',
              'yyyy-MM-dd'
            )
            await prefill.postPrefillDobAuth(
              advertisementId,
              traversalId,
              convertedDob
            )
            onNext()
            break
          }

          case 'overview': {
            trackPrefill(AmplitudeEvent.PrefillOverviewWeiter)
            const pageName = await decideNextPageAfterDobAuth(rootStore)
            if (pageName) {
              history.replace(pageName)
              navigate(pageName, { replace: true })
            } else {
              triggerOnNextWithFinalize()
            }
            break
          }
        }
      } catch (err) {
        logAndTrackError(err as Error)
      } finally {
        rootStore.setLoadingMode(LoadingMode.None)
      }
    }

    const handleSecondaryButtonClick = async () => {
      if (currentPageInSession === 'requestPin') {
        trackPrefill(AmplitudeEvent.PrefillManualEntryClicked)
      }

      if (currentPageInSession === 'authenticatePin') {
        trackPinCheck('escape')
        trackInAmplitude(AmplitudeEvent.PrefillSecondaryButtonClicked)
      }

      if (currentPageInSession === 'authenticateDob') {
        trackDobCheck('escape')
        trackInAmplitude(AmplitudeEvent.PrefillSecondaryButtonClicked)
      }

      if (currentPageInSession === 'overview') {
        trackPrefill(AmplitudeEvent.PrefillCheckDataClicked)
      }

      prefill.setIsPrefillSkipped(true)
      await traversal.patch([
        { op: 'set', path: '/common/hasAcceptedTerms', value: true }
      ])
      traversal.acceptTermsAndConditions()
      onNext()
    }

    const handleBackButtonClick = () => {
      onBack?.()
    }

    const triggerOnNextWithFinalize = () => {
      prefill.setIsFinalForced(true)
      onNext()
    }

    const handleTimeout = () => {
      trackPrefill(AmplitudeEvent.PrefillGoToOffersAfterTimeout)
      triggerOnNextWithFinalize()
    }

    const continueButtonText = prefill.isError
      ? 'Daten selbst eingeben'
      : CONTINUE_BUTTON_TEXT[currentPageInSession ?? 'Weiter']

    const secondaryButtonText = 'Daten selbst eingeben'

    const isBackButtonHidden =
      currentPageInSession === 'verifiedPin' ||
      currentPageInSession === 'authenticatePin' ||
      currentPageInSession === 'authenticateDob' ||
      currentPageInSession === 'overview' ||
      prefill.isError

    const isCaptionVisible = currentPageInSession === 'requestPin'

    const { sizeOfContinueButton, sizeOfSecondaryButton, sizeOfBackButton } =
      getButtonSizes(isBackButtonHidden)

    const [previousErrorPageName, setPreviousErrorPageName] = useState<string>()

    useAsyncEffect(async () => {
      const previousErrorPageNameFlag = await fieldErrors.findFirstErrorPage()

      setPreviousErrorPageName(previousErrorPageNameFlag)
    }, [loadingMode])

    if (previousErrorPageName) {
      trackInAmplitude(AmplitudeEvent.PreviousPageErrorNavigationShown)
      return (
        <PreviousPageErrors
          navigate={navigate}
          pageName={previousErrorPageName}
        />
      )
    }

    return (
      <CategoryLayout ref={ref}>
        {currentPageInSession === 'verifiedPin' && !prefill.isError && (
          <TimeoutTrigger onTimeout={handleTimeout} />
        )}
        {message && <HotlineFallback message={message} />}
        <Grid
          container
          spacing={1}
          direction={isMobile ? 'column' : 'row-reverse'}
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            className={flexBasisGrid}
            container
            item
            spacing={1}
            direction={isMobile ? 'column' : 'row-reverse'}
            xs={12}
          >
            {/* CONTINUE BUTTON */}
            <Grid item xs={12} sm={sizeOfContinueButton} mb={1}>
              <Button
                className={button}
                size="large"
                fullWidth
                onClick={handleContinueButtonClick}
                id="next-submit"
                name="next-submit"
                variant="contained"
                color="primary"
                endIcon={
                  loadingMode === LoadingMode.None ? (
                    <ArrowForwardIcon />
                  ) : undefined
                }
                sx={{
                  height: '48px',
                  fontSize: 16
                }}
                disabled={
                  hasCooldown ||
                  loadingMode === LoadingMode.Blocking ||
                  (currentPageInSession === 'authenticatePin' &&
                    (prefill.pin.length !== PIN_LENGTH ||
                      prefill.isPinInvalid) &&
                    !prefill.isError) ||
                  (currentPageInSession === 'authenticateDob' &&
                    prefill.dob.length !== DOB_LENGTH)
                }
              >
                {continueButtonText}
              </Button>
            </Grid>

            <Grid item xs={12}>
              {currentPageInSession === 'authenticatePin' &&
                !prefill.isError && (
                  <Grid item xs={12} mt={0} mb={2} pt={0}>
                    <ResendPin setPin={prefill.setPin} isMobileHidden={true} />
                  </Grid>
                )}
            </Grid>

            {/* SECONDARY BUTTON */}
            {!prefill.isError && (
              <Grid
                item
                xs={12}
                sm={sizeOfSecondaryButton}
                data-testid="grid-item-next-submit-container"
                display="flex"
                alignItems="center"
                fontWeight={500}
              >
                <Button
                  className={button}
                  size="large"
                  fullWidth
                  variant={'outlined'}
                  color="primary"
                  disabled={hasCooldown}
                  onClick={handleSecondaryButtonClick}
                  endIcon={
                    loadingMode === LoadingMode.None ? (
                      <ArrowForwardIcon />
                    ) : undefined
                  }
                  sx={{
                    height: '48px',
                    fontSize: 16
                  }}
                >
                  {secondaryButtonText}
                </Button>
              </Grid>
            )}

            {/* BACK BUTTON */}
            {!isBackButtonHidden && (
              <Grid item xs={12} sm={sizeOfBackButton}>
                <Button
                  className={button}
                  size="large"
                  onClick={handleBackButtonClick}
                  type="button"
                  id="back-submit"
                  name="back-submit"
                  color="primary"
                  sx={{
                    height: '48px',
                    fontSize: 16
                  }}
                  variant={'outlined'}
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                >
                  Zurück
                </Button>
              </Grid>
            )}

            {/* CAPTION */}
            {isCaptionVisible && !prefill.isError && (
              <Grid item>
                <Typography variant="caption">
                  Ich willige ein, dass meine Daten aus der jüngsten Anfrage
                  innerhalb der letzten 4 Jahre zum Vorausfüllen der
                  Antragsstrecke verwendet werden. Ich kann meine freiwillige
                  Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.{' '}
                  <Link
                    onClick={() =>
                      trackInAmplitude(
                        AmplitudeEvent.PrefillDataProtectionClicked
                      )
                    }
                    href={clientDetails?.urls?.dataProtection}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    Mehr Infos
                  </Link>
                  .
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CategoryLayout>
    )
  }
)

export default memo(PrefillFooter)
